import React from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Mail, Wrench, Clock } from "lucide-react";
import { COMPANY, SERVICE_AREAS } from "../mock";
import GoogleGuaranteeBadge from "./GoogleGuaranteeBadge";
import PaymentMethods from "./PaymentMethods";
import { SOCIAL_PROFILES } from "../lib/seo";

const PROFILE_LABELS = { googleBusinessProfile: "Google Business Profile", facebook: "Facebook", bingPlaces: "Bing Places", appleMaps: "Apple Maps", threads: "Threads", instagram: "Instagram" };

const Footer = () => {
  return (
    <footer className="bg-slate-950 text-slate-300">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="h-11 w-11 rounded-xl bg-orange-500 flex items-center justify-center">
                <Wrench className="h-6 w-6 text-white" strokeWidth={2.5} />
              </div>
              <div className="leading-tight">
                <div
                  className="text-[16px] font-extrabold"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800 }}
                >
                  <span className="text-white">DECATUR</span>{" "}
                  <span className="text-orange-400">SOLUTION</span>{" "}
                  <span className="text-white">WORKS</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                  Appliance Repair
                </div>
              </div>
            </div>
            <p className="text-slate-400 leading-relaxed text-[15px] mb-5">
              Expert appliance repair in Decatur GA, Stone Mountain & DeKalb County. Same-day service for washing machines, dryers, refrigerators, and ovens.
            </p>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="inline-flex items-center gap-2 text-xl font-extrabold text-orange-400 hover:text-orange-300 transition-colors"
            >
              <Phone className="h-5 w-5" />
              {COMPANY.phone}
            </a>
            <div className="mt-5">
              <GoogleGuaranteeBadge variant="full" />
            </div>
            <div className="mt-6" data-testid="footer-find-us">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">Find Decatur Solution Works On</h3>
              <ul className="space-y-1 text-sm">
                {Object.entries(SOCIAL_PROFILES).filter(([, url]) => url).map(([key, url]) => (
                  <li key={key}>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid={`footer-profile-${key}`}>
                      {PROFILE_LABELS[key] || key}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="text-white font-bold mb-4 text-xs uppercase tracking-widest">Brand Specialists</div>
            <ul className="space-y-2.5">
              {[
                ["Samsung Repair", "/samsung"], ["LG Repair", "/lg"], ["Whirlpool Repair", "/whirlpool"], ["GE Repair", "/ge"],
                ["Frigidaire Repair", "/frigidaire"], ["Maytag Repair", "/maytag"], ["Bosch Repair", "/bosch"], ["KitchenAid Repair", "/kitchenaid"],
                ["Kenmore Repair", "/kenmore"], ["Sub-Zero Repair", "/sub-zero"], ["Wolf Repair", "/wolf"], ["True Commercial Repair", "/true"],
              ].map(([b, href]) => (
                <li key={b}><Link to={href} className="text-slate-400 hover:text-orange-400 text-[14px]">{b}</Link></li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-white font-bold mb-4 text-xs uppercase tracking-widest">Services</div>
            <ul className="space-y-2.5">
              {[
                ["Refrigerator Repair", "/refrigerator-repair"], ["Fridge Not Cooling But Lights On", "/fridge-not-cooling-but-lights-on"],
                ["Refrigerator Leaking Water", "/refrigerator-leaking-water"], ["Washing Machine Repair", "/washing-machine-repair"],
                ["Washer Won't Drain", "/washer-wont-drain"], ["Dryer Repair / Not Heating", "/dryer-repair-not-heating"],
                ["Oven Repair", "/oven-repair"], ["Stove Repair", "/stove-repair"], ["Dishwasher Not Draining", "/dishwasher-repair-not-draining"],
                ["Ice Maker Not Working", "/refrigerator-ice-maker-not-working"], ["Refrigerator Not Dispensing Water", "/refrigerator-not-dispensing-water"],
                ["Water Line Installation & Hookup", "/refrigerator-water-line-installation"], ["Refrigerator Water Line Repair", "/refrigerator-water-line-repair"],
              ].map(([t, href]) => (
                <li key={href}><Link to={href} className="text-slate-400 hover:text-orange-400 text-[14px]">{t}</Link></li>
              ))}
              <li><Link to="/blog" className="text-slate-400 hover:text-orange-400 text-[14px]">Blog</Link></li>
              <li><Link to="/guides" className="text-slate-400 hover:text-orange-400 text-[14px]" data-testid="footer-guides-link">Diagnostic Guides</Link></li>
              <li><Link to="/careers" data-testid="footer-careers-link" className="text-slate-400 hover:text-orange-400 text-[14px]">Careers</Link></li>
              <li><Link to="/free-checklist" data-testid="footer-free-checklist-link" className="text-slate-400 hover:text-orange-400 text-[14px]">Free Home Appliance Care Checklist</Link></li>
              <li className="pt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Commercial</li>
              <li><Link to="/commercial-refrigeration" data-testid="footer-commercial-refrigeration-link" className="text-slate-400 hover:text-orange-400 text-[14px]">Commercial Refrigeration Repair</Link></li>
              <li><Link to="/commercial-refrigeration-rack-repair" className="text-slate-400 hover:text-orange-400 text-[14px]">Refrigeration &amp; Rack Repairs</Link></li>
              <li><Link to="/commercial-appliance-repair" className="text-slate-400 hover:text-orange-400 text-[14px]">Commercial Appliance Repair</Link></li>
              <li><Link to="/preventive-maintenance" data-testid="footer-pm-plans-link" className="text-slate-400 hover:text-orange-400 text-[14px]">Preventive Maintenance Plans</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-white font-bold mb-4 text-xs uppercase tracking-widest">Contact</div>
            <ul className="space-y-3 text-[14px]">
              <li className="flex items-start gap-2.5">
                <Phone className="h-4 w-4 text-orange-400 mt-0.5 flex-shrink-0" />
                <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-white">{COMPANY.phone}</a>
              </li>
              <li className="flex items-start gap-2.5">
                <Mail className="h-4 w-4 text-orange-400 mt-0.5 flex-shrink-0" />
                <Link to="/contact#book" className="hover:text-white" data-testid="footer-book-online-link">Book Online / Send a Message</Link>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 text-orange-400 mt-0.5 flex-shrink-0" />
                <span>{COMPANY.address}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Clock className="h-4 w-4 text-orange-400 mt-0.5 flex-shrink-0" />
                <span>{COMPANY.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-slate-800">
          <div className="text-white font-bold mb-3 text-xs uppercase tracking-widest">Service Areas</div>
          <p className="text-slate-400 text-[13.5px] leading-relaxed" data-testid="footer-service-areas">
            {SERVICE_AREAS.map((c, i) => {
              const slug = c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
              return (
                <React.Fragment key={c}>
                  {i > 0 && " • "}
                  <Link to={`/appliance-repair-${slug}`} className="text-slate-400 hover:text-orange-400" data-testid={`footer-area-${slug}`}>{c}</Link>
                </React.Fragment>
              );
            })}
          </p>
        </div>

        <div className="mt-8 pt-6 border-t border-slate-800">
          <div className="text-white font-bold mb-3 text-xs uppercase tracking-widest">We Accept</div>
          <PaymentMethods compact />
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[13px] text-slate-400">
          <div>© {new Date().getFullYear()} Decatur Solution Works. All rights reserved.</div>
          <div className="flex items-center gap-5">
            <Link to="/blog" className="hover:text-slate-300">Blog</Link>
            <Link to="/careers" className="hover:text-slate-300">Careers</Link>
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-slate-300">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-slate-300">Terms &amp; Warranty</Link>
            <Link to="/admin" className="hover:text-slate-300">Admin</Link>
            <Link to="/contact" data-testid="footer-contact-link" className="hover:text-slate-300">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
